/**
 * 8.5 — Option activée en semaine 1 → rétention J+14.
 *
 * ⚠️⚠️ C'est l'analyse la plus facile à mal lire de tout le dashboard, et la
 * seule qui puisse conduire à une mauvaise décision produit.
 *
 * Elle montre que les comptes ayant activé une option en semaine 1 reviennent
 * plus (ou moins) à J+14. Elle ne montre PAS que l'option retient. Les options
 * ne sont pas attribuées au hasard : qui découvre le mode boucle en semaine 1
 * est déjà quelqu'un qui explore les réglages, donc déjà quelqu'un
 * d'impliqué. Le lift mesure une association, jamais l'effet qu'aurait le fait
 * de pousser l'option à tout le monde.
 *
 * Deux séries — avec et sans — donc deux emplacements catégoriels et une
 * légende obligatoire.
 */

import { MIN_GROUP_SIZE } from '@shared/types/stats';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { formatInteger, formatPercent } from '../../lib/format';
import { AXIS_STYLE, BAR_MAX_SIZE, COLORS } from '../../lib/theme';
import type { DataColumn, FeatureAnalysis, FeatureLiftRow } from '../../types';
import { ChartCard } from '../Card';
import { ChartLegend, ChartTooltip, type LegendEntry } from '../ChartTooltip';
import { ReadingBox } from '../ReadingBox';
import { StatTile } from '../StatTile';

const legend: readonly LegendEntry[] = [
  { label: 'Option activée en S1', color: COLORS.series1, shape: 'area' },
  { label: 'Sans l\'option', color: COLORS.series2, shape: 'area' },
];

const dash = (value: number | null) => (value === null ? '—' : formatPercent(value));

const columns: ReadonlyArray<DataColumn<FeatureLiftRow>> = [
  { key: 'label', header: 'Option', render: (row) => row.label },
  { key: 'nWith', header: 'Avec', numeric: true, render: (row) => formatInteger(row.usersWithFeature) },
  { key: 'nWithout', header: 'Sans', numeric: true, render: (row) => formatInteger(row.usersWithout) },
  { key: 'rWith', header: 'J+14 avec', numeric: true, render: (row) => dash(row.rateWith) },
  { key: 'rWithout', header: 'J+14 sans', numeric: true, render: (row) => dash(row.rateWithout) },
  {
    key: 'lift',
    header: 'Écart',
    numeric: true,
    render: (row) =>
      row.liftPoints === null
        ? '—'
        : `${row.liftPoints > 0 ? '+' : ''}${row.liftPoints.toLocaleString('fr-FR')} pts`,
  },
];

export function FeaturesPanel({ data }: { data: FeatureAnalysis }) {
  const ranked = [...data.features]
    .filter((row) => row.liftPoints !== null)
    .sort((a, b) => (b.liftPoints ?? 0) - (a.liftPoints ?? 0));
  const top = ranked[0];

  return (
    <>
      <ReadingBox
        method={
          <>
            Comptes ayant au moins quatorze jours d'ancienneté observée, répartis selon qu'ils
            ont activé l'option au moins une fois pendant leur <strong>semaine 1</strong> (J0 à
            J+6, relative à leur entrée). Rétention mesurée à J+14 <strong>exactement</strong>.
            Un groupe de moins de {MIN_GROUP_SIZE} comptes n'est pas publié. Les comptes n'ayant
            fait aucun apprentissage en semaine 1 comptent parmi ceux « sans l'option » — ils ne
            l'ont pas activée, c'est la question posée.
          </>
        }
      >
        {data.eligibleUsers === 0
          ? 'Aucun compte n\'a assez d\'ancienneté sur cette période.'
          : top && top.liftPoints !== null
            ? `L'écart le plus marqué est sur « ${top.label} » : ${dash(top.rateWith)} de retour à J+14 chez ceux qui l'ont activée en semaine 1, contre ${dash(top.rateWithout)} chez les autres, soit ${top.liftPoints > 0 ? '+' : ''}${top.liftPoints} points.`
            : 'Aucun groupe n\'atteint la taille minimale pour être publié.'}
        {' '}
        <strong>Corrélation, pas causalité.</strong> Ces options ne sont pas tirées au sort : qui
        les découvre en semaine 1 est déjà quelqu'un qui explore. Pousser l'option à tout le
        monde ne reproduirait pas cet écart.
      </ReadingBox>

      <div className="kpi-row">
        <StatTile
          label="Comptes analysés"
          value={formatInteger(data.eligibleUsers)}
          hint="au moins 14 jours d'ancienneté observée"
        />
        {top && top.liftPoints !== null ? (
          <StatTile
            label="Écart le plus marqué"
            value={`${top.liftPoints > 0 ? '+' : ''}${top.liftPoints} pts`}
            hint={top.label}
          />
        ) : null}
      </div>

      <div className="chart-grid">
        <div className="chart-grid__wide">
          <ChartCard
            title="Rétention exacte à J+14, avec et sans l'option en semaine 1"
            subtitle="Association, pas effet — voir la note de méthode"
            emptyMessage="Aucun groupe publiable sur cette période."
            rows={data.features}
            columns={columns}
            chartHeight={300}
          >
            <ChartLegend entries={legend} />
            <ResponsiveContainer width="100%" height="88%">
              <BarChart
                data={data.features as FeatureLiftRow[]}
                layout="vertical"
                margin={{ top: 4, right: 24, bottom: 0, left: 0 }}
              >
                <CartesianGrid stroke={COLORS.grid} horizontal={false} />
                <XAxis
                  type="number"
                  tickFormatter={(value: number) => `${value} %`}
                  tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="label"
                  tick={{ fill: COLORS.textSecondary, fontSize: AXIS_STYLE.fontSize }}
                  axisLine={false}
                  tickLine={false}
                  width={150}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                  content={<ChartTooltip valueFormatter={formatPercent} />}
                />
                <Bar isAnimationActive={false} dataKey="rateWith" name="Option activée en S1" fill={COLORS.series1} radius={[0, 4, 4, 0]} maxBarSize={BAR_MAX_SIZE} />
                <Bar isAnimationActive={false} dataKey="rateWithout" name="Sans l'option" fill={COLORS.series2} radius={[0, 4, 4, 0]} maxBarSize={BAR_MAX_SIZE} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>
    </>
  );
}
